import { useCallback, useEffect, useState } from 'react'
import {
  Button,
  Card,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd'
import { ThunderboltOutlined } from '@ant-design/icons'
import { adminApi } from '../../api'
import { KNOWLEDGE_SCOPE_OPTIONS_SHORT, type KnowledgeScope } from '../../constants/knowledgeScope'
import { tablePagination, useServerPagination } from '../../components/ui'
import { knowledgeDocPreviewPath } from '../../lib/knowledgeDocPreview'

type LinkRule = {
  id: number
  scope: string
  rule_type: string
  src_key: string
  tgt_key: string
  match_value: string | null
  status: string
  source: string
  evidence_json?: Record<string, unknown> | null
  created_by_name?: string | null
  review_note?: string | null
}

type Recommendation = {
  id: number
  scope: string
  rule_type: string
  src_key: string
  tgt_key: string
  sample_value: string
  co_doc_count: number
  key_set_json?: string[] | null
  batch_id: string
  status: string
}

type SharedDocItem = {
  item_id: number
  title: string
  matched_keys: string
}

const PREVIEW_DOC_LIMIT = 5

const STATUS_COLOR: Record<string, string> = {
  pending: 'gold',
  approved: 'green',
  rejected: 'red',
  disabled: 'default',
  open: 'blue',
  accepted: 'green',
  dismissed: 'default',
}

function ruleLabel(r: LinkRule) {
  if (r.rule_type === 'key_alias') {
    return `${r.src_key} ↔ ${r.tgt_key}`
  }
  const mv = r.match_value ? `（${r.match_value}）` : ''
  return `${r.src_key}${mv}`
}

export function LinkRulesManage() {
  const [scope, setScope] = useState<KnowledgeScope>('enterprise')
  const [ruleStatus, setRuleStatus] = useState<string>('pending')
  const [rules, setRules] = useState<LinkRule[]>([])
  const rulePag = useServerPagination()
  const recPag = useServerPagination()
  const docsPag = useServerPagination()
  const [ruleTotal, setRuleTotal] = useState(0)
  const [rulesLoading, setRulesLoading] = useState(false)

  const [recs, setRecs] = useState<Recommendation[]>([])
  const [recTotal, setRecTotal] = useState(0)
  const [recsLoading, setRecsLoading] = useState(false)
  const [mining, setMining] = useState(false)
  const [minCoDocs, setMinCoDocs] = useState(3)

  const [rejectOpen, setRejectOpen] = useState(false)
  const [rejectId, setRejectId] = useState<number | null>(null)
  const [rejectNote, setRejectNote] = useState('')

  const [docsOpen, setDocsOpen] = useState(false)
  const [docsRec, setDocsRec] = useState<Recommendation | null>(null)
  const [docsItems, setDocsItems] = useState<SharedDocItem[]>([])
  const [docsTotal, setDocsTotal] = useState(0)
  const [docsLoading, setDocsLoading] = useState(false)
  const [docsFullList, setDocsFullList] = useState(false)

  const loadRules = useCallback(() => {
    setRulesLoading(true)
    adminApi
      .get<{ items: LinkRule[]; total: number }>('/admin/link-rules', {
        params: { scope, status: ruleStatus, page: rulePag.page, page_size: rulePag.pageSize },
      })
      .then((r) => {
        setRules(r.data.items)
        setRuleTotal(r.data.total)
      })
      .catch((e) => message.error(e.message))
      .finally(() => setRulesLoading(false))
  }, [scope, ruleStatus, rulePag.page, rulePag.pageSize])

  const loadRecs = useCallback(() => {
    setRecsLoading(true)
    adminApi
      .get<{ items: Recommendation[]; total: number }>('/admin/link-rules/recommendations', {
        params: { scope, status: 'open', page: recPag.page, page_size: recPag.pageSize },
      })
      .then((r) => {
        setRecs(r.data.items)
        setRecTotal(r.data.total)
      })
      .catch((e) => message.error(e.message))
      .finally(() => setRecsLoading(false))
  }, [scope, recPag.page, recPag.pageSize])

  const loadSharedDocs = useCallback(
    (rec: Recommendation, page: number, fullList: boolean, pageSize = docsPag.pageSize) => {
      setDocsLoading(true)
      adminApi
        .get<{ items: SharedDocItem[]; total: number; sample_value: string }>(
          `/admin/link-rules/recommendations/${rec.id}/documents`,
          {
            params: {
              page,
              page_size: fullList ? pageSize : PREVIEW_DOC_LIMIT,
            },
          },
        )
        .then((r) => {
          setDocsItems(r.data.items)
          setDocsTotal(r.data.total)
        })
        .catch((e) => message.error(e.message))
        .finally(() => setDocsLoading(false))
    },
    [docsPag.pageSize],
  )

  const openSharedDocs = (rec: Recommendation) => {
    const useFull = rec.co_doc_count <= PREVIEW_DOC_LIMIT
    setDocsRec(rec)
    docsPag.resetPage()
    setDocsFullList(useFull)
    setDocsOpen(true)
    loadSharedDocs(rec, 1, useFull)
  }

  const showFullDocList = () => {
    if (!docsRec) return
    setDocsFullList(true)
    docsPag.resetPage()
    loadSharedDocs(docsRec, 1, true)
  }

  useEffect(() => {
    loadRules()
  }, [loadRules])

  useEffect(() => {
    loadRecs()
  }, [loadRecs])

  const approveRule = (id: number) => {
    adminApi
      .post(`/admin/link-rules/${id}/approve`, { note: '' })
      .then(() => {
        message.success('已通过')
        loadRules()
      })
      .catch((e) => message.error(e.message))
  }

  const submitReject = () => {
    if (rejectId == null) return
    adminApi
      .post(`/admin/link-rules/${rejectId}/reject`, { note: rejectNote })
      .then(() => {
        message.success('已驳回')
        setRejectOpen(false)
        setRejectId(null)
        setRejectNote('')
        loadRules()
      })
      .catch((e) => message.error(e.message))
  }

  const runMine = () => {
    setMining(true)
    adminApi
      .post<{ batch_id: string; created: number }>('/admin/link-rules/recommendations/mine', {
        scope,
        min_co_docs: minCoDocs,
      })
      .then((r) => {
        message.success(`分析完成，新增 ${r.data.created} 条推荐（批次 ${r.data.batch_id}）`)
        loadRecs()
      })
      .catch((e) => message.error(e.message))
      .finally(() => setMining(false))
  }

  return (
    <>
      <Space style={{ marginBottom: 16 }} wrap>
        <Select
          value={scope}
          style={{ width: 140 }}
          options={KNOWLEDGE_SCOPE_OPTIONS_SHORT}
          onChange={(v) => {
            setScope(v)
            rulePag.resetPage()
            recPag.resetPage()
          }}
        />
      </Space>
      <Tabs
        items={[
          {
            key: 'rules',
            label: '关联规则审核',
            children: (
              <Card>
                <Typography.Paragraph type="secondary">
                  用户从文档预览页提议的跨属性关联规则；通过后参与相关文档推荐排序加权。
                </Typography.Paragraph>
                <Space style={{ marginBottom: 12 }} wrap>
                  <Select
                    value={ruleStatus}
                    style={{ width: 120 }}
                    options={[
                      { value: 'pending', label: '待审核' },
                      { value: 'approved', label: '已生效' },
                      { value: 'rejected', label: '已驳回' },
                      { value: 'disabled', label: '已禁用' },
                    ]}
                    onChange={(v) => {
                      setRuleStatus(v)
                      rulePag.resetPage()
                    }}
                  />
                </Space>
                <Table
                  rowKey="id"
                  size="small"
                  loading={rulesLoading}
                  dataSource={rules}
                  pagination={tablePagination(
                    rulePag.page,
                    ruleTotal,
                    rulePag.handlePaginationChange,
                    rulePag.paginationOptions,
                  )}
                  columns={[
                    { title: 'ID', dataIndex: 'id', width: 64 },
                    {
                      title: '规则',
                      render: (_: unknown, r: LinkRule) => ruleLabel(r),
                    },
                    {
                      title: '类型',
                      dataIndex: 'rule_type',
                      width: 100,
                      render: (v: string) => (v === 'key_alias' ? '跨属性' : '同值'),
                    },
                    {
                      title: '来源',
                      dataIndex: 'source',
                      width: 100,
                      render: (v: string) =>
                        v === 'user_suggest' ? '用户提议' : v === 'system_mine' ? '系统挖掘' : v,
                    },
                    {
                      title: '状态',
                      dataIndex: 'status',
                      width: 88,
                      render: (v: string) => <Tag color={STATUS_COLOR[v]}>{v}</Tag>,
                    },
                    {
                      title: '操作',
                      width: 160,
                      render: (_: unknown, r: LinkRule) =>
                        r.status === 'pending' ? (
                          <Space size="small">
                            <Button type="link" size="small" onClick={() => approveRule(r.id)}>
                              通过
                            </Button>
                            <Button
                              type="link"
                              size="small"
                              danger
                              onClick={() => {
                                setRejectId(r.id)
                                setRejectOpen(true)
                              }}
                            >
                              驳回
                            </Button>
                          </Space>
                        ) : r.status === 'approved' ? (
                          <Popconfirm
                            title="禁用此规则？"
                            onConfirm={() =>
                              adminApi
                                .post(`/admin/link-rules/${r.id}/disable`)
                                .then(() => {
                                  message.success('已禁用')
                                  loadRules()
                                })
                                .catch((e) => message.error(e.message))
                            }
                          >
                            <Button type="link" size="small" danger>
                              禁用
                            </Button>
                          </Popconfirm>
                        ) : null,
                    },
                  ]}
                />
              </Card>
            ),
          },
          {
            key: 'recs',
            label: '规则推荐',
            children: (
              <Card>
                <Typography.Paragraph type="secondary">
                  扫描全库 for_relation 属性：同一 value 出现在多个 key 下且公共文档数达到阈值时，推荐建立跨属性别名规则。
                </Typography.Paragraph>
                <Space style={{ marginBottom: 12 }} wrap>
                  <span>最少公共文档数</span>
                  <Input
                    type="number"
                    min={2}
                    max={100}
                    value={minCoDocs}
                    style={{ width: 80 }}
                    onChange={(e) => setMinCoDocs(Number(e.target.value) || 3)}
                  />
                  <Button
                    type="primary"
                    icon={<ThunderboltOutlined />}
                    loading={mining}
                    onClick={runMine}
                  >
                    立即分析
                  </Button>
                </Space>
                <Table
                  rowKey="id"
                  size="small"
                  loading={recsLoading}
                  dataSource={recs}
                  pagination={tablePagination(
                    recPag.page,
                    recTotal,
                    recPag.handlePaginationChange,
                    recPag.paginationOptions,
                  )}
                  columns={[
                    { title: 'ID', dataIndex: 'id', width: 64 },
                    {
                      title: '推荐规则',
                      render: (_: unknown, r: Recommendation) => `${r.src_key} ↔ ${r.tgt_key}`,
                    },
                    {
                      title: '共同关联属性值',
                      dataIndex: 'sample_value',
                      ellipsis: true,
                    },
                    {
                      title: '公共文档数',
                      dataIndex: 'co_doc_count',
                      width: 100,
                      render: (count: number, r: Recommendation) =>
                        count > 0 ? (
                          <Button type="link" size="small" onClick={() => openSharedDocs(r)}>
                            {count}
                          </Button>
                        ) : (
                          count
                        ),
                    },
                    {
                      title: '涉及属性',
                      render: (_: unknown, r: Recommendation) =>
                        (r.key_set_json || []).join(' / '),
                      ellipsis: true,
                    },
                    {
                      title: '操作',
                      width: 140,
                      render: (_: unknown, r: Recommendation) => (
                        <Space size="small">
                          <Button
                            type="link"
                            size="small"
                            onClick={() =>
                              adminApi
                                .post(`/admin/link-rules/recommendations/${r.id}/accept`)
                                .then(() => {
                                  message.success('已采纳并生效')
                                  loadRecs()
                                  loadRules()
                                })
                                .catch((e) => message.error(e.message))
                            }
                          >
                            采纳
                          </Button>
                          <Button
                            type="link"
                            size="small"
                            onClick={() =>
                              adminApi
                                .post(`/admin/link-rules/recommendations/${r.id}/dismiss`)
                                .then(() => {
                                  message.success('已忽略')
                                  loadRecs()
                                })
                                .catch((e) => message.error(e.message))
                            }
                          >
                            忽略
                          </Button>
                        </Space>
                      ),
                    },
                  ]}
                />
              </Card>
            ),
          },
        ]}
      />
      <Modal
        title="驳回规则"
        open={rejectOpen}
        onOk={submitReject}
        onCancel={() => {
          setRejectOpen(false)
          setRejectId(null)
        }}
        okText="驳回"
        okButtonProps={{ danger: true }}
      >
        <Input.TextArea
          rows={3}
          placeholder="驳回原因（选填）"
          value={rejectNote}
          onChange={(e) => setRejectNote(e.target.value)}
        />
      </Modal>
      <Modal
        title="公共文档"
        open={docsOpen}
        width={720}
        footer={null}
        onCancel={() => {
          setDocsOpen(false)
          setDocsRec(null)
          setDocsItems([])
          setDocsTotal(0)
          docsPag.resetPage()
          setDocsFullList(false)
        }}
      >
        {docsRec ? (
          <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
            共同关联属性值：<Typography.Text code>{docsRec.sample_value}</Typography.Text>
            {' · '}
            推荐规则：{docsRec.src_key} ↔ {docsRec.tgt_key}
          </Typography.Paragraph>
        ) : null}
        <Table
          rowKey="item_id"
          size="small"
          loading={docsLoading}
          dataSource={docsItems}
          pagination={
            docsFullList
              ? tablePagination(docsPag.page, docsTotal, (p, ps) => {
                  docsPag.handlePaginationChange(p, ps)
                  if (docsRec) loadSharedDocs(docsRec, p, true, ps ?? docsPag.pageSize)
                }, docsPag.paginationOptions)
              : false
          }
          columns={[
            { title: 'ID', dataIndex: 'item_id', width: 72 },
            {
              title: '标题',
              dataIndex: 'title',
              ellipsis: true,
              render: (title: string, row: SharedDocItem) => {
                const previewScope: KnowledgeScope =
                  docsRec?.scope === 'personal' ? 'personal' : scope
                return (
                  <a
                    href={knowledgeDocPreviewPath(row.item_id, previewScope)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {title}
                  </a>
                )
              },
            },
            { title: '匹配属性', dataIndex: 'matched_keys', ellipsis: true },
          ]}
        />
        {!docsFullList && docsTotal > PREVIEW_DOC_LIMIT ? (
          <div style={{ marginTop: 12, textAlign: 'center' }}>
            <Typography.Text type="secondary">
              共 {docsTotal} 篇，已显示前 {PREVIEW_DOC_LIMIT} 篇
            </Typography.Text>
            <Button type="link" onClick={showFullDocList}>
              查看更多
            </Button>
          </div>
        ) : null}
      </Modal>
    </>
  )
}
